import { useState } from "react";
import { CalendarDays, Clock, Search, MessageCircle, Shield, Medal, Footprints } from "lucide-react";
import { Reveal } from "./Reveal";
import SectionHeading from "./SectionHeading";
import { HORARIOS, MODALIDADES, WHATSAPP_URL } from "../data/siteData";

const MOD_ICONS = [Shield, Medal, Footprints];

const badgeCampo = (campo) =>
  campo === "natural" ? (
    <span className="rounded-full bg-lawn/15 px-3 py-1 text-[11px] font-bold uppercase tracking-wider text-lawn">
      Campo Natural
    </span>
  ) : (
    <span className="rounded-full bg-royal/50 px-3 py-1 text-[11px] font-bold uppercase tracking-wider text-blue-200">
      Campo Sintético
    </span>
  );

const Categorias = () => {
  const [ano, setAno] = useState("");
  const [resultado, setResultado] = useState(null);

  const buscar = (e) => {
    e.preventDefault();
    const y = parseInt(ano, 10);
    if (!y) return;
    const turma = HORARIOS.find((h) => h.anos.includes(y));
    if (turma) {
      setResultado(turma.id);
      setTimeout(() => {
        document
          .getElementById(`card-${turma.id}`)
          ?.scrollIntoView({ behavior: "smooth", block: "center" });
      }, 120);
    } else {
      setResultado("fora");
    }
  };

  return (
    <section id="categorias" data-testid="categorias-section" className="relative bg-navy py-20 md:py-28">
      <div className="turf-lines pointer-events-none absolute inset-0" aria-hidden="true" />
      <div className="relative mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
        <SectionHeading
          eyebrow="Temporada 2026"
          title="Programação de Treinos 2026"
          lead="Turmas do SUB 5 ao SUB 15 em campo natural e sintético. Encontre a turma ideal para o seu filho."
        />

        <Reveal className="mx-auto mb-14 max-w-2xl">
          <form
            onSubmit={buscar}
            data-testid="category-filter-form"
            className="flex flex-col gap-3 rounded-2xl border border-white/10 bg-night/80 p-5 backdrop-blur sm:flex-row sm:items-center"
          >
            <label htmlFor="ano-nascimento" className="text-xs font-bold uppercase tracking-[0.2em] text-gold sm:whitespace-nowrap">
              Descubra a turma do seu filho
            </label>
            <div className="flex flex-1 gap-3">
              <input
                id="ano-nascimento"
                data-testid="category-filter-year"
                type="number"
                min="2008"
                max="2022"
                placeholder="Ano de nascimento (ex.: 2015)"
                value={ano}
                onChange={(e) => {
                  setAno(e.target.value);
                  setResultado(null);
                }}
                className="w-full flex-1 rounded-full border border-white/15 bg-navy px-5 py-3 text-sm text-white placeholder:text-white/40 focus:border-lawn focus:outline-none"
              />
              <button
                type="submit"
                data-testid="category-filter-submit"
                className="inline-flex items-center gap-2 rounded-full bg-gold px-6 py-3 text-sm font-bold uppercase tracking-wide text-navy transition-transform duration-200 hover:scale-105"
              >
                <Search size={16} strokeWidth={2.5} />
                <span className="hidden sm:inline">Descobrir</span>
              </button>
            </div>
            {resultado === "fora" && (
              <p data-testid="category-filter-empty" className="text-xs text-gold sm:absolute">
                Não encontramos turma para esse ano. Fale conosco no WhatsApp!
              </p>
            )}
          </form>
        </Reveal>

        <div className="grid gap-6 md:grid-cols-2">
          {HORARIOS.map((h, i) => (
            <Reveal key={h.id} delay={(i % 2) * 0.08}>
              <article
                id={`card-${h.id}`}
                data-testid={`category-card-${h.id}`}
                className={`group h-full rounded-2xl border bg-night/70 p-6 backdrop-blur transition-all duration-300 hover:-translate-y-1.5 md:p-8 ${
                  resultado === h.id
                    ? "border-gold shadow-[0_0_50px_rgba(250,204,21,0.25)] ring-2 ring-gold"
                    : "border-white/10 hover:border-lawn/50"
                }`}
              >
                <div className="mb-4 flex items-start justify-between gap-3">
                  <div>
                    <h3 className="font-display text-4xl uppercase tracking-wide text-white transition-colors group-hover:text-lawn">
                      {h.categoria}
                    </h3>
                    <p className="mt-1 text-sm font-semibold text-gold">Nascidos em {h.anosLabel}</p>
                  </div>
                  {badgeCampo(h.campo)}
                </div>
                <div className="flex flex-wrap gap-x-6 gap-y-2 text-sm text-white/75">
                  <span className="inline-flex items-center gap-2">
                    <CalendarDays size={16} className="text-lawn" /> {h.dias}
                  </span>
                  <span className="inline-flex items-center gap-2">
                    <Clock size={16} className="text-lawn" /> {h.horario}
                  </span>
                </div>
                <a
                  href={`${WHATSAPP_URL}?text=${encodeURIComponent(
                    `Olá! Quero garantir uma vaga na turma ${h.categoria} (${h.anosLabel}) — ${h.dias}, às ${h.horario}.`
                  )}`}
                  target="_blank"
                  rel="noopener noreferrer"
                  data-testid={`category-cta-${h.id}`}
                  className="mt-6 inline-flex items-center gap-2 text-sm font-bold uppercase tracking-wide text-lawn transition-colors hover:text-gold"
                >
                  <MessageCircle size={16} /> Garantir vaga
                </a>
              </article>
            </Reveal>
          ))}
        </div>

        <div className="mt-20">
          <SectionHeading eyebrow="Modalidades especiais" title="Além das categorias" />
          <div className="grid gap-6 md:grid-cols-3">
            {MODALIDADES.map((m, i) => {
              const Icon = MOD_ICONS[i];
              return (
                <Reveal key={m.nome} delay={i * 0.1}>
                  <article
                    data-testid={`modalidade-card-${i}`}
                    className="group flex h-full flex-col rounded-2xl border border-white/10 bg-gradient-to-b from-royal/40 to-night p-7 transition-all duration-300 hover:-translate-y-1.5 hover:border-gold/50"
                  >
                    <Icon size={34} strokeWidth={1.8} className="mb-4 text-gold transition-transform duration-300 group-hover:scale-110" />
                    <h3 className="font-display text-2xl uppercase tracking-wide text-white">{m.nome}</h3>
                    <p className="mt-3 flex-1 text-sm leading-relaxed text-white/70">{m.descricao}</p>
                    <a
                      href={`${WHATSAPP_URL}?text=${encodeURIComponent(`Olá! Quero saber mais sobre: ${m.nome}.`)}`}
                      target="_blank"
                      rel="noopener noreferrer"
                      data-testid={`modalidade-cta-${i}`}
                      className="mt-6 inline-flex items-center justify-center gap-2 rounded-full border-2 border-lawn px-5 py-2.5 text-xs font-bold uppercase tracking-wide text-lawn transition-colors duration-200 hover:bg-lawn hover:text-navy"
                    >
                      <MessageCircle size={15} /> Quero saber mais
                    </a>
                  </article>
                </Reveal>
              );
            })}
          </div>
        </div>
      </div>
    </section>
  );
};

export default Categorias;
